import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { catchError, from, of, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthLogoutService } from 'src/app/services/auth/auth-logout.service';
import { HTTP_STATUS } from '../constants/http-status.constants';

let isUnauthorizedLogoutInProgress = false;

/** @internal Resets module state between Jest tests. */
export function resetUnauthorizedLogoutStateForTests(): void {
  isUnauthorizedLogoutInProgress = false;
}

function shouldSkipTokenForRequest(url: string): boolean {
  // Never append access token for Auth0 endpoints.
  // Important: `''.includes('') === true`, so guard against empty/misconfigured domains.
  const auth0Domain = (environment.auth0.domain ?? '').trim();
  if (!auth0Domain) {
    return false;
  }

  try {
    const parsed = new URL(url, globalThis.location.origin);
    return parsed.hostname === auth0Domain;
  } catch {
    // If parsing fails, fall back to a conservative check.
    return url.includes(auth0Domain);
  }
}

export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
  if (shouldSkipTokenForRequest(req.url)) {
    return next(req);
  }

  const reqWithApimHeader = req.clone({
    setHeaders: {
      'Ocp-Apim-Subscription-Key': environment['Ocp-Apim-Subscription-Key'] ?? '',
    },
  });

  const auth = inject(AuthService);
  const authLogout = inject(AuthLogoutService);

  return from(
    auth.getAccessTokenSilently({
      authorizationParams: {
        audience: environment.auth0.audience,
      },
    }),
  ).pipe(
    // Only swallow token-retrieval failures (e.g., public endpoints / no session).
    // Must stay BEFORE switchMap so it doesn't catch HTTP errors from next(...).
    catchError(() => of<string | null>(null)),
    switchMap((token) => {
      if (!token) {
        return next(reqWithApimHeader);
      }

      const authReq = reqWithApimHeader.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
      return next(authReq);
    }),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && !isUnauthorizedLogoutInProgress) {
        // 401: expired/invalid session. 403: region mismatch (e.g. AU user on the NZ URL).
        // Both force a logout that also clears client storage.
        if (error.status === HTTP_STATUS.UNAUTHORIZED) {
          isUnauthorizedLogoutInProgress = true;
          authLogout.logout({ reason: 'expired' });
        } else if (error.status === HTTP_STATUS.FORBIDDEN) {
          isUnauthorizedLogoutInProgress = true;
          authLogout.logout({ reason: 'forbidden' });
        }
      }

      return throwError(() => error);
    }),
  );
};
